import colors from "tailwindcss/colors";

export interface Branch {
  name: string;
  abbreviation: string;
  label: string;
  colorName?: string;
  colorShade?: number;
}

// Available Tailwind colors for branches (excluding grays/neutrals for variety)
const AVAILABLE_COLORS = [
  "lime", "green", "emerald", "teal", "cyan", "sky",
  "blue", "indigo", "violet", "purple", "fuchsia", "pink", "rose"
] as const;

// Default fallback shade
const DEFAULT_FALLBACK_SHADE = 500;

/**
 * Composable for accessing dynamic branch configuration from database
 *
 * Replaces static imports from branches.ts with API-fetched data
 * Provides same helper functions with reactive data
 */
export function useBranches() {
  // Fetch branches from API with caching
  const { data: branches, pending, error } = useLazyFetch<Branch[]>("/api/branches", {
    key: "branches",
    // Cache data in Nuxt payload for SSR
    getCachedData: (key) => useNuxtApp().payload.data[key] || useNuxtApp().static.data[key]
  });

  // Helper: Get branch by name
  const getBranch = (branchName: string): Branch | undefined => {
    return branches.value?.find(b => b.name === branchName);
  };

  // Helper: Get branch abbreviation
  const getBranchAbbr = (branchName: string): string => {
    return getBranch(branchName)?.abbreviation || branchName.charAt(0).toUpperCase();
  };

  // Helper: Get branch label for display
  const getBranchLabel = (branchName: string): string => {
    return getBranch(branchName)?.label || branchName;
  };

  // Helper: Get branch color value from Tailwind colors
  const getBranchColorValue = (branchName: string): string => {
    const branch = getBranch(branchName);

    // Helper to get fallback color dynamically based on branch name hash
    const getFallbackColor = (name: string) => {
      // Simple hash: sum of char codes
      const hash = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
      const colorIndex = hash % AVAILABLE_COLORS.length;
      const fallbackColorName = AVAILABLE_COLORS[colorIndex] as keyof typeof colors;
      const fallbackPalette = colors[fallbackColorName];
      return fallbackPalette?.[DEFAULT_FALLBACK_SHADE] as string;
    };

    if (!branch) {
      // Fallback for unknown branches - pick color based on name hash
      return getFallbackColor(branchName);
    }

    // Use branch's colorShade if specified, otherwise default to 500
    const shade = branch.colorShade || 500;

    // If colorName is not specified, use array index to pick from AVAILABLE_COLORS
    if (!branch.colorName) {
      const branchIndex = branches.value?.findIndex(b => b.name === branchName) ?? -1;
      const colorIndex = branchIndex % AVAILABLE_COLORS.length;
      const autoColorName = AVAILABLE_COLORS[colorIndex] as keyof typeof colors;
      const palette = colors[autoColorName];
      return (palette?.[shade as keyof typeof palette] as string) || getFallbackColor(branchName);
    }

    // Get color palette based on branch's colorName
    const colorName = branch.colorName as keyof typeof colors;
    const palette = colors[colorName];

    // Return specified shade, or fallback if palette doesn't exist
    return (palette?.[shade as keyof typeof palette] as string) || getFallbackColor(branchName);
  };

  // Get branch color in OKLCH format (for use in Vue styles)
  const getBranchColor = (branchName: string): string => {
    return getBranchColorValue(branchName);
  };

  // Get branch color as hex without # prefix (for external APIs like Dicebear)
  const getBranchHexColor = (branchName: string): string => {
    const colorValue = getBranchColorValue(branchName);
    return toHex(colorValue).replace("#", "");
  };

  return {
    branches,
    pending,
    error,
    getBranch,
    getBranchAbbr,
    getBranchLabel,
    getBranchColor,
    getBranchHexColor
  };
}
